import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import LoyaltyTierMessageBanner from 'loyalty/components/LoyaltyTierMessageBanner'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIntl } from 'react-intl'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useMemo } from 'react'

const imageParams: ImageParams = {
  mobileWidth: '560px',
  width: '100%',
  fit: 'center',
  objectFit: 'contain',
}

function LoyaltyEsimBenefitsInfoModal() {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const tierDisplayName = tier ? getLoyaltyTierDisplayName(tier) : ''
  const benefit = useLoyaltyUserBenefitView('esim_package')
  const gbValue = benefit?.benefit?.value?.amount
  const image: App.Image = useMemo(() => ({
    id: 'vumiz1mqwf0srfjhzk88',
    title: intl.formatMessage({
      id: 'lMx41KP3-R_ZK4IdaBSQ',
      defaultMessage: 'Free travel eSIM for Société members',
      description: 'Image title for the loyalty eSIM benefit illustration in the info modal',
    }),
  }), [intl])

  if (!gbValue) return null

  return (
    <ModalBase size="S">
      <ModalHeader title={intl.formatMessage({
        id: '_3TzApdi0Hmdoe7ZYZHi',
        defaultMessage: '{gbValue}GB Free Roaming Data for Société {tierName} members',
        description: 'Title for the generic eSIM benefit info modal showing data amount and tier',
      }, { gbValue, tierName: tierDisplayName })}
      />
      <ModalBody>
        <ModalImageThumbnail image={image} imageParams={imageParams} />
        <ModalContent>
          <Group direction="vertical" gap={24}>

            {tier && (
              <LoyaltyTierMessageBanner
                tier={tier}
                density="compact"
                icon={<LoyaltyTierIcon tier={tier} size={24} />}
                endContent={
                  <CSSBreakpoint min="tablet">
                    <BodyText variant="small">
                      {intl.formatMessage({
                        id: 'Ay2TQdEyW64P1EJeAyMz',
                        defaultMessage: 'Unlocks 7 days before departure',
                        description: 'Text indicating when the eSIM promo code becomes available',
                      })}
                    </BodyText>
                  </CSSBreakpoint>
                }
              >
                <Group direction="vertical" gap={4}>
                  <span>
                    {intl.formatMessage({
                      id: '1tCGnoR0IdiHlnmMiQsk',
                      defaultMessage: 'Available to redeem',
                      description: 'Prefix label for the loyalty benefit availability status message',
                    })}{' '}
                    <BodyText variant="medium" weight="bold" as="span">
                      {intl.formatMessage({
                        id: 'gHrSUWIF-bAKTZpgIWOE',
                        defaultMessage: 'Unlimited',
                        description: 'Label displayed when the loyalty benefit availability is unlimited',
                      })}
                    </BodyText>
                  </span>
                  <CSSBreakpoint max="mobile">
                    <BodyText variant="small">
                      {intl.formatMessage({
                        id: 'Ay2TQdEyW64P1EJeAyMz',
                        defaultMessage: 'Unlocks 7 days before departure',
                        description: 'Text indicating when the eSIM promo code becomes available',
                      })}
                    </BodyText>
                  </CSSBreakpoint>
                </Group>
              </LoyaltyTierMessageBanner>
            )}

            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'jG7wlVr_7JPA_lFm5-O1',
                defaultMessage: 'As a Société {tierName} member, you\'ll receive a {gbValue}GB Free Roaming Data on every eligible international booking — so you\'re connected the moment you arrive.',
                description: 'Intro description of the eSIM benefit in the details modal',
              }, { gbValue, tierName: tierDisplayName })}
            </BodyText>

            <VerticalSpacer gap={4}>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: 'LBwlvbQkyeCotDSLCAY-',
                  defaultMessage: 'How it works',
                  description: 'Section heading explaining how the eSIM benefit works for members',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '6sdr6QstMm2dBPLWKHy5',
                  defaultMessage: 'Activate your eSIM before you fly and enjoy data as soon as you land.',
                  description: 'Sub-description explaining eSIM activation steps for how it works',
                })}
              </BodyText>
              <BodyText variant="medium" as="div">
                <List>
                  <li>
                    {intl.formatMessage({
                      id: '5O9dSjlN5P-nWz2AuJjd',
                      defaultMessage: 'No roaming fees',
                      description: 'eSIM benefit bullet point in the info modal - no roaming fees',
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: 'QsOIkiZl7K7GwBTAwuup',
                      defaultMessage: 'No physical SIM required',
                      description: 'eSIM benefit bullet point in the info modal - no physical SIM required',
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: '1K6P4zDcdgSfHp1E17I_',
                      defaultMessage: 'Keep your number',
                      description: 'eSIM benefit bullet point in the info modal - keep your number',
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: 'V9_Cw0neKBC74miSr590',
                      defaultMessage: 'Works instantly',
                      description: 'eSIM benefit bullet point in the info modal - works instantly',
                    })}
                  </li>
                </List>
              </BodyText>
            </VerticalSpacer>

            <VerticalSpacer gap={8}>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: '8p-UryUcscC0mxlDcyJj',
                  defaultMessage: 'What is an eSIM?',
                  description: 'Section heading explaining what an eSIM is in the benefit info modal',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'yjZ-x_THDCoK6pwWVXFB',
                  defaultMessage: 'An eSIM is a digital SIM that lets you access mobile data overseas — no physical SIM card required.',
                  description: 'Description of what an eSIM is and how it works without a physical SIM',
                })}
              </BodyText>
            </VerticalSpacer>

          </Group>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default LoyaltyEsimBenefitsInfoModal
